// Табличные отчёты экрана АПП (письмо Карасайской ЦРБ, ноябрь 2026):
//   • заболевания по МКБ-10: класс → код → перечень случаев
//   • подразделение → специалист: количество и сумма по источникам финансирования
//   • виды услуг ЕПС («лоты», Приложение 4) — определены правилами на бэкенде
//   • соисполнители таблицей (вместо пончика Сл5) и «наша организация как соисполнитель»
//
// Бэкенд: dashboards/backend/app/routers/app_reports.py (/api/v1/app/report/*).
// Контейнер слайда и функция запроса приходят параметрами из DashboardsApp.tsx —
// так нет циклического импорта.
import React, { useEffect, useMemo, useState } from "react";

export type ReportFilters = {
  mis_id?: string;
  finance_source_ids?: number[];
  year?: number;
  month?: number;
  day?: number;
  date_from?: string;
  date_to?: string;
  soi_id?: number;
  treatment_reason?: { code: number; purpose: number } | null;
  department_id?: number;
  performer_employee?: string;
  mkb10_id?: number;
};

type Fetcher = <T>(path: string) => Promise<T>;
type Container = (props: { title: string; hintRight?: string; children: React.ReactNode }) => React.ReactElement;

type FinanceCell = { count: number; amount: number };

const C = {
  border: "rgba(255,255,255,0.14)",
  head: "#12385f",
  muted: "rgba(255,255,255,0.65)",
  accent: "#48B1CC",
  rowHover: "rgba(255,255,255,0.06)",
};

const fmtInt = (n: number) => Math.round(n || 0).toLocaleString("ru-RU");
const fmtMoney = (n: number) => Math.round(n || 0).toLocaleString("ru-RU");

function reportQuery(f: ReportFilters, extra: Record<string, string | number | undefined | null> = {}) {
  const qs = new URLSearchParams();
  const set = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  };
  set("mis_id", f.mis_id);
  set("year", f.year);
  set("month", f.month);
  set("day", f.day);
  set("date_from", f.date_from);
  set("date_to", f.date_to);
  (f.finance_source_ids || []).forEach((id) => Number.isFinite(id) && qs.append("finance_source_id", String(id)));
  set("department_id", f.department_id);
  set("performer_employee", f.performer_employee);
  set("mkb10_id", f.mkb10_id);
  if (f.treatment_reason?.code !== undefined) set("tr_code", f.treatment_reason.code);
  if (f.treatment_reason?.purpose !== undefined) set("tr_purpose", f.treatment_reason.purpose);
  Object.entries(extra).forEach(([k, v]) => set(k, v));
  return qs.toString();
}

function useReport<T>(fetcher: Fetcher, path: string, filters: ReportFilters, extra: Record<string, any> = {}) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const qs = useMemo(() => reportQuery(filters, extra), [filters, JSON.stringify(extra)]);

  useEffect(() => {
    if (!filters.mis_id) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetcher<T>(`${path}?${qs}`)
      .then((d) => !cancelled && setData(d))
      .catch((e: any) => {
        if (cancelled) return;
        // 404 — бэкенд ещё не перезапущен с новыми ручками; не пугаем ошибкой
        const msg = String(e?.message || e);
        setError(/\b404\b/.test(msg) ? "Отчёт появится после обновления сервера" : msg);
        setData(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [fetcher, path, qs, filters.mis_id]);

  return { data, loading, error };
}

function downloadCsv(name: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? "");
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = "﻿" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const btn: React.CSSProperties = {
  height: 26,
  padding: "0 10px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.28)",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
};

const th: React.CSSProperties = {
  position: "sticky",
  top: 0,
  background: C.head,
  color: "#fff",
  fontWeight: 700,
  fontSize: 12,
  padding: "6px 8px",
  borderBottom: `1px solid ${C.border}`,
  textAlign: "left",
  whiteSpace: "nowrap",
  zIndex: 1,
};
const td: React.CSSProperties = {
  padding: "5px 8px",
  borderBottom: `1px solid ${C.border}`,
  fontSize: 12,
  verticalAlign: "top",
};
const tdNum: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

function Status({ loading, error, empty }: { loading: boolean; error: string; empty: boolean }) {
  if (loading) return <div style={{ fontSize: 12, color: C.muted }}>Загрузка…</div>;
  if (error) return <div style={{ fontSize: 12, color: "#fca5a5" }}>{error}</div>;
  if (empty) return <div style={{ fontSize: 12, color: C.muted }}>За выбранный период данных нет</div>;
  return null;
}

function Toolbar({ children, note }: { children?: React.ReactNode; note?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0", flexWrap: "wrap" }}>
      {children}
      {note ? <span style={{ fontSize: 11, color: C.muted, flex: 1, minWidth: 200 }}>{note}</span> : null}
    </div>
  );
}

function FinanceHead({ finances }: { finances: string[] }) {
  return (
    <>
      {finances.map((f) => (
        <th key={f} style={{ ...th, textAlign: "center" }} colSpan={2}>
          {f}
        </th>
      ))}
      <th style={{ ...th, textAlign: "center" }} colSpan={2}>
        Итого
      </th>
    </>
  );
}
function FinanceSubHead({ finances }: { finances: string[] }) {
  return (
    <>
      {[...finances, "Итого"].map((f) => (
        <React.Fragment key={f}>
          <th style={{ ...th, top: 27, textAlign: "right" }}>кол-во</th>
          <th style={{ ...th, top: 27, textAlign: "right" }}>сумма, ₸</th>
        </React.Fragment>
      ))}
    </>
  );
}
function FinanceCells({ finances, by, count, amount, bold }: {
  finances: string[];
  by: Record<string, FinanceCell>;
  count: number;
  amount: number;
  bold?: boolean;
}) {
  const st = bold ? { ...tdNum, fontWeight: 700 } : tdNum;
  return (
    <>
      {finances.map((f) => (
        <React.Fragment key={f}>
          <td style={st}>{by[f] ? fmtInt(by[f].count) : ""}</td>
          <td style={st}>{by[f] ? fmtMoney(by[f].amount) : ""}</td>
        </React.Fragment>
      ))}
      <td style={st}>{fmtInt(count)}</td>
      <td style={st}>{fmtMoney(amount)}</td>
    </>
  );
}
const financeCsvHeader = (finances: string[]) =>
  [...finances, "Итого"].flatMap((f) => [`${f}: кол-во`, `${f}: сумма`]);
const financeCsvCells = (finances: string[], by: Record<string, FinanceCell>, count: number, amount: number) => [
  ...finances.flatMap((f) => [by[f]?.count ?? "", by[f] ? Math.round(by[f].amount) : ""]),
  count,
  Math.round(amount),
];

const scrollBox: React.CSSProperties = { flex: 1, minHeight: 0, overflow: "auto" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", color: "#fff" };

// ────────────────────────────────────────────────────────────────────────────
// Подразделение → специалист
// ────────────────────────────────────────────────────────────────────────────
type DeptSpecRow = {
  department_id: number;
  department: string;
  count: number;
  amount: number;
  by_finance: Record<string, FinanceCell>;
  specialties: { specialty: string; count: number; amount: number; by_finance: Record<string, FinanceCell> }[];
};

export function DeptSpecialtySlide({ filters, fetcher, Container }: { filters: ReportFilters; fetcher: Fetcher; Container: Container }) {
  const { data, loading, error } = useReport<{ finances: string[]; rows: DeptSpecRow[]; note: string; period: { from: string; to: string } }>(
    fetcher, "/api/v1/app/report/dept-specialty", filters,
  );
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [allOpen, setAllOpen] = useState(false);
  const finances = data?.finances || [];
  const rows = data?.rows || [];

  const exportCsv = () =>
    downloadCsv("podrazdeleniya_specialisty", ["Подразделение", "Специалист", ...financeCsvHeader(finances)], rows.flatMap((d) => [
      [d.department, "ВСЕГО", ...financeCsvCells(finances, d.by_finance, d.count, d.amount)],
      ...d.specialties.map((s) => [d.department, s.specialty, ...financeCsvCells(finances, s.by_finance, s.count, s.amount)]),
    ]));

  return (
    <Container title="Услуги по подразделениям и специалистам: количество и сумма" hintRight="Клик по подразделению — раскрыть специалистов">
      <Toolbar note={data ? `${data.period.from} — ${data.period.to}. ${data.note}` : undefined}>
        <button style={btn} onClick={() => setAllOpen((v) => !v)}>{allOpen ? "Свернуть все" : "Раскрыть все"}</button>
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!!data && !rows.length} />
      <div style={scrollBox}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>Подразделение / специалист</th>
              <FinanceHead finances={finances} />
            </tr>
            <tr>
              <FinanceSubHead finances={finances} />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const isOpen = allOpen || !!open[d.department_id];
              return (
                <React.Fragment key={d.department_id}>
                  <tr style={{ cursor: "pointer", background: "rgba(255,255,255,0.04)" }}
                      onClick={() => setOpen((o) => ({ ...o, [d.department_id]: !o[d.department_id] }))}>
                    <td style={{ ...td, fontWeight: 700 }}>{isOpen ? "▾" : "▸"} {d.department}</td>
                    <FinanceCells finances={finances} by={d.by_finance} count={d.count} amount={d.amount} bold />
                  </tr>
                  {isOpen &&
                    d.specialties.map((s) => (
                      <tr key={s.specialty}>
                        <td style={{ ...td, paddingLeft: 24 }}>{s.specialty}</td>
                        <FinanceCells finances={finances} by={s.by_finance} count={s.count} amount={s.amount} />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Виды услуг ЕПС («лоты»)
// ────────────────────────────────────────────────────────────────────────────
type KindRow = { code: string; name: string; count: number; amount: number; by_finance: Record<string, FinanceCell> };

export function ServiceKindsSlide({ filters, fetcher, Container }: { filters: ReportFilters; fetcher: Fetcher; Container: Container }) {
  const { data, loading, error } = useReport<{ finances: string[]; rows: KindRow[]; note: string; period: { from: string; to: string } }>(
    fetcher, "/api/v1/app/report/service-kinds", filters,
  );
  const finances = data?.finances || [];
  const rows = data?.rows || [];
  const total = rows.reduce(
    (acc, r) => {
      acc.count += r.count;
      acc.amount += r.amount;
      for (const [f, v] of Object.entries(r.by_finance)) {
        const t = (acc.by[f] ||= { count: 0, amount: 0 });
        t.count += v.count;
        t.amount += v.amount;
      }
      return acc;
    },
    { count: 0, amount: 0, by: {} as Record<string, FinanceCell> },
  );

  const exportCsv = () =>
    downloadCsv("vidy_uslug_eps", ["Код вида", "Вид услуги", ...financeCsvHeader(finances)],
      rows.map((r) => [r.code, r.name, ...financeCsvCells(finances, r.by_finance, r.count, r.amount)]));

  return (
    <Container title="Услуги по видам (лотам) ЕПС с источником финансирования">
      <Toolbar note={data ? `${data.period.from} — ${data.period.to}. ${data.note}` : undefined}>
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!!data && !rows.length} />
      <div style={scrollBox}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>Код</th>
              <th style={th} rowSpan={2}>Вид услуги</th>
              <FinanceHead finances={finances} />
            </tr>
            <tr>
              <FinanceSubHead finances={finances} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{/^\d+$/.test(r.code) ? r.code : "—"}</td>
                <td style={td}>{r.name}</td>
                <FinanceCells finances={finances} by={r.by_finance} count={r.count} amount={r.amount} />
              </tr>
            ))}
            {rows.length > 0 && (
              <tr>
                <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Всего</td>
                <FinanceCells finances={finances} by={total.by} count={total.count} amount={total.amount} bold />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Соисполнители (таблица вместо пончика Сл5)
// ────────────────────────────────────────────────────────────────────────────
type SoiRow = { soi_id: number; soi: string; count: number; amount: number; docs: number; by_finance: Record<string, FinanceCell> };

export function SoiTableSlide({ filters, fetcher, Container, title, selectedSoiId, onSelect, onOpenTable }: {
  filters: ReportFilters;
  fetcher: Fetcher;
  Container: Container;
  title: string;
  selectedSoiId?: number;
  onSelect: (soiId: number) => void;
  onOpenTable: (soiId: number, name: string) => void;
}) {
  // фильтр по самому соисполнителю к этой таблице не применяем — иначе она схлопнется в одну строку
  const f = useMemo(() => ({ ...filters, soi_id: undefined }), [filters]);
  const { data, loading, error } = useReport<{ finances: string[]; rows: SoiRow[]; period: { from: string; to: string } }>(
    fetcher, "/api/v1/app/report/soi", f,
  );
  const finances = data?.finances || [];
  const rows = data?.rows || [];

  const exportCsv = () =>
    downloadCsv("soispolniteli", ["Соисполнитель", ...financeCsvHeader(finances)],
      rows.map((r) => [r.soi, ...financeCsvCells(finances, r.by_finance, r.count, r.amount)]));

  return (
    <Container title={title} hintRight="Клик: выбрать соисполнителя • ⊞: таблица услуг">
      <Toolbar note={data ? `${data.period.from} — ${data.period.to}` : undefined}>
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!!data && !rows.length} />
      <div style={scrollBox}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>Соисполнитель</th>
              <FinanceHead finances={finances} />
              <th style={th} rowSpan={2} />
            </tr>
            <tr>
              <FinanceSubHead finances={finances} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.soi_id} onClick={() => onSelect(r.soi_id)}
                  style={{ cursor: "pointer", background: selectedSoiId === r.soi_id ? "rgba(72,177,204,0.25)" : undefined }}>
                <td style={td}>{r.soi}</td>
                <FinanceCells finances={finances} by={r.by_finance} count={r.count} amount={r.amount} />
                <td style={{ ...td, width: 30 }}>
                  <button style={{ ...btn, height: 22, padding: "0 6px" }} title="Таблица услуг"
                          onClick={(e) => { e.stopPropagation(); onOpenTable(r.soi_id, r.soi); }}>⊞</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

type ProviderRow = { mis_id: string; customer: string; count: number; amount: number; docs: number; by_finance: Record<string, FinanceCell> };

export function SoiProviderSlide({ filters, fetcher, Container }: { filters: ReportFilters; fetcher: Fetcher; Container: Container }) {
  // к чужим данным применимы только период и источник финансирования — остальное бэкенд не берёт
  const { data, loading, error } = useReport<{ finances: string[]; rows: ProviderRow[]; matched_soi: string[]; note: string; period: { from: string; to: string } }>(
    fetcher, "/api/v1/app/report/soi-provider", filters,
  );
  const finances = data?.finances || [];
  const rows = data?.rows || [];

  const exportCsv = () =>
    downloadCsv("my_soispolnitel", ["Организация-заказчик", ...financeCsvHeader(finances)],
      rows.map((r) => [r.customer, ...financeCsvCells(finances, r.by_finance, r.count, r.amount)]));

  return (
    <Container title="Наша организация как соисполнитель: услуги, оказанные по заказу других организаций">
      <Toolbar note={data ? `${data.period.from} — ${data.period.to}. ${data.note || ""}` : undefined}>
        <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV</button>
      </Toolbar>
      {data?.matched_soi?.length ? (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>В справочнике соисполнителей: {data.matched_soi.join("; ")}</div>
      ) : null}
      <Status loading={loading} error={error} empty={!!data && !rows.length} />
      <div style={scrollBox}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>Организация-заказчик</th>
              <FinanceHead finances={finances} />
            </tr>
            <tr>
              <FinanceSubHead finances={finances} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.mis_id}>
                <td style={td}>{r.customer}</td>
                <FinanceCells finances={finances} by={r.by_finance} count={r.count} amount={r.amount} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Заболевания по МКБ-10: класс → код → случаи
// ────────────────────────────────────────────────────────────────────────────
type MkbCode = { mkb10_id: number | null; code: string; name: string; count: number; docs: number };
type MkbChapter = { range: string; name: string; count: number; docs: number; codes: MkbCode[] };
type CaseRow = {
  doc_id: string;
  date: string | null;
  mkb10: string | null;
  mkb10_name: string | null;
  department: string | null;
  doctor: string | null;
  specialty: string | null;
  services: string | null;
  finance: string | null;
  age: number | null;
  amount: number;
};

export function MkbReportSlide({ filters, fetcher, Container }: { filters: ReportFilters; fetcher: Fetcher; Container: Container }) {
  // фильтр по одному коду МКБ (клик в Сл9) к перечню не применяем — иначе в нём одна строка
  const f = useMemo(() => ({ ...filters, mkb10_id: undefined }), [filters]);
  const { data, loading, error } = useReport<{ chapters: MkbChapter[]; note: string; period: { from: string; to: string } }>(
    fetcher, "/api/v1/app/report/mkb10", f,
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [cases, setCases] = useState<{ title: string; extra: Record<string, string | number> } | null>(null);

  const chapters = useMemo(() => {
    const q = search.trim().toLowerCase();
    const src = data?.chapters || [];
    if (!q) return src;
    return src
      .map((ch) => ({ ...ch, codes: ch.codes.filter((c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)) }))
      .filter((ch) => ch.codes.length || ch.name.toLowerCase().includes(q));
  }, [data, search]);

  const exportCsv = () =>
    downloadCsv("zabolevaniya_mkb10", ["Класс", "Код МКБ-10", "Наименование", "Случаев", "Услуг"],
      (data?.chapters || []).flatMap((ch) => ch.codes.map((c) => [ch.range, c.code, c.name, c.docs, c.count])));

  return (
    <Container title="Посещения по заболеваниям (МКБ-10): классы, коды и случаи" hintRight="Клик по классу — коды • «случаи» — перечень приёмов">
      <Toolbar note={data ? `${data.period.from} — ${data.period.to}. ${data.note}` : undefined}>
        <input
          placeholder="Поиск: код или название"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ height: 26, borderRadius: 6, border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.08)", color: "#fff", padding: "0 8px", fontSize: 12, width: 220 }}
        />
        <button style={btn} onClick={exportCsv} disabled={!data?.chapters?.length}>CSV</button>
      </Toolbar>
      <Status loading={loading} error={error} empty={!!data && !data.chapters.length} />
      <div style={scrollBox}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Класс / код</th>
              <th style={th}>Наименование</th>
              <th style={{ ...th, textAlign: "right" }}>Случаев</th>
              <th style={{ ...th, textAlign: "right" }}>Услуг</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {chapters.map((ch) => {
              const isOpen = !!search.trim() || !!open[ch.range];
              return (
                <React.Fragment key={ch.range}>
                  <tr style={{ cursor: "pointer", background: "rgba(255,255,255,0.04)" }} onClick={() => setOpen((o) => ({ ...o, [ch.range]: !o[ch.range] }))}>
                    <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{isOpen ? "▾" : "▸"} {ch.range}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{ch.name}</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(ch.docs)}</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{fmtInt(ch.count)}</td>
                    <td style={td}>
                      {ch.range !== "—" && (
                        <button style={{ ...btn, height: 22 }} onClick={(e) => { e.stopPropagation(); setCases({ title: `${ch.range} ${ch.name}`, extra: { chapter: ch.range } }); }}>
                          случаи
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen &&
                    ch.codes.map((c) => (
                      <tr key={`${ch.range}-${c.code}`}>
                        <td style={{ ...td, paddingLeft: 24, whiteSpace: "nowrap" }}>{c.code}</td>
                        <td style={td}>{c.name || "—"}</td>
                        <td style={tdNum}>{fmtInt(c.docs)}</td>
                        <td style={tdNum}>{fmtInt(c.count)}</td>
                        <td style={td}>
                          {c.mkb10_id != null && (
                            <button style={{ ...btn, height: 22 }} onClick={() => setCases({ title: `${c.code} ${c.name}`, extra: { mkb10_id: c.mkb10_id as number } })}>
                              случаи
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {cases && <CasesModal title={cases.title} filters={f} extra={cases.extra} fetcher={fetcher} onClose={() => setCases(null)} />}
    </Container>
  );
}

function CasesModal({ title, filters, extra, fetcher, onClose }: {
  title: string;
  filters: ReportFilters;
  extra: Record<string, string | number>;
  fetcher: Fetcher;
  onClose: () => void;
}) {
  const PAGE = 100;
  const [page, setPage] = useState(1);
  const { data, loading, error } = useReport<{ total: number; rows: CaseRow[] }>(
    fetcher, "/api/v1/app/report/mkb10-cases", filters, { ...extra, page, page_size: PAGE },
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  const exportCsv = () =>
    downloadCsv("sluchai_mkb10", ["Дата", "МКБ-10", "Диагноз", "Подразделение", "Врач", "Должность", "Услуги", "Источник", "Возраст", "Сумма"],
      rows.map((r) => [r.date ? r.date.slice(0, 10) : "", r.mkb10 || "", r.mkb10_name || "", r.department || "", r.doctor || "", r.specialty || "", r.services || "", r.finance || "", r.age ?? "", Math.round(r.amount)]));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,5,20,0.6)", zIndex: 20000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#0e2b4b", color: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, width: "min(1400px, calc(100vw - 32px))", height: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>Случаи: {title}</h3>
          <button style={btn} onClick={exportCsv} disabled={!rows.length}>CSV (страница)</button>
          <button style={btn} onClick={onClose}>Закрыть</button>
        </div>
        <Toolbar note={`Всего случаев: ${fmtInt(total)}. Один случай — один документ приёма. Перехода в документ МИС здесь нет: МИС не отдаёт ссылки по номерам документов этого потока.`}>
          <button style={btn} disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>← назад</button>
          <span style={{ fontSize: 12 }}>стр. {page} из {pages}</span>
          <button style={btn} disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>вперёд →</button>
        </Toolbar>
        <Status loading={loading} error={error} empty={!!data && !rows.length} />
        <div style={scrollBox}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {["Дата", "МКБ-10", "Диагноз", "Подразделение", "Врач", "Должность", "Услуги", "Источник", "Возраст", "Сумма, ₸"].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.doc_id}>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{r.date ? new Date(r.date).toLocaleDateString("ru-RU") : ""}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{r.mkb10}</td>
                  <td style={td}>{r.mkb10_name}</td>
                  <td style={td}>{r.department}</td>
                  <td style={td}>{r.doctor}</td>
                  <td style={td}>{r.specialty}</td>
                  <td style={td}>{r.services}</td>
                  <td style={td}>{r.finance}</td>
                  <td style={tdNum}>{r.age ?? ""}</td>
                  <td style={tdNum}>{fmtMoney(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Общие кирпичи — их же использует HospitalReportSlides.tsx (отчёты стационара).
export type { Fetcher, Container };
export { useReport, downloadCsv, btn, th, td, tdNum, C, fmtInt, Status, Toolbar, scrollBox, tableStyle };
